import { useState } from 'react';
import { Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { UserRole } from '@/types';
import { validatePhoneNumber } from '@/utils/validation';

interface AddUserDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (userData: { 
    username: string; 
    phoneNumber: string; 
    password: string;
    role: UserRole 
  }) => void;
}

export function AddUserDialog({
  open,
  onClose,
  onSubmit,
}: AddUserDialogProps) {
  const [formData, setFormData] = useState({
    username: '',
    phoneNumber: '',
    password: '',
    role: 'user' as UserRole,
  });
  const [error, setError] = useState('');

  const handleSubmit = () => {
    // Validate required fields
    if (!formData.username || !formData.phoneNumber || !formData.password) {
      setError('جميع الحقول مطلوبة');
      return;
    }

    // Validate phone number
    const phoneValidation = validatePhoneNumber(formData.phoneNumber);
    if (phoneValidation !== true) {
      setError(phoneValidation);
      return;
    }

    // Validate password
    if (formData.password.length < 4 || !/[a-zA-z0-9]$/.test(formData.password)) {
      setError('يجب أن تتكون كلمة المرور من 4 أرقام فقط');
      return;
    }

    onSubmit(formData);
    setFormData({ username: '', phoneNumber: '', password: '', role: 'user' });
    setError('');
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>إضافة مستخدم جديد</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="username">اسم المستخدم</Label>
            <Input
              id="username"
              value={formData.username}
              onChange={(e) => setFormData(prev => ({ ...prev, username: e.target.value }))}
              placeholder="أدخل اسم المستخدم"
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="phoneNumber">رقم الهاتف</Label>
            <Input
              id="phoneNumber"
              type="tel"
              value={formData.phoneNumber}
              onChange={(e) => setFormData(prev => ({ ...prev, phoneNumber: e.target.value }))}
              dir="ltr"
              className="text-left"
              placeholder="009665xxxxxxxx, 009659xxxxxxx"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="password">كلمة المرور</Label>
            <Input
              id="password"
              type="text"
              value={formData.password}
              onChange={(e) => setFormData(prev => ({ ...prev, password: e.target.value }))}
              dir="ltr"
              className="text-left"
              placeholder="******"
              maxLength={15}
            />
            <p className="text-xs text-gray-500">
              يجب أن تتكون كلمة المرور من 4 أرقام فقط
            </p>
          </div>

          <div className="space-y-2">
            <Label>نوع المستخدم</Label>
            <RadioGroup
              value={formData.role}
              onValueChange={(value: UserRole) => setFormData(prev => ({ ...prev, role: value }))}
              className="flex gap-4"
            >
              <div className="flex items-center space-x-2 space-x-reverse">
                <RadioGroupItem value="user" id="user" />
                <Label htmlFor="user">مستخدم عادي</Label>
              </div>
              <div className="flex items-center space-x-2 space-x-reverse">
                <RadioGroupItem value="admin" id="admin" />
                <Label htmlFor="admin">مدير</Label>
              </div>
            </RadioGroup>
          </div>

          {error && (
            <p className="text-sm text-red-600 text-center">
              {error}
            </p>
          )}
        </div>
        <DialogFooter className="sm:justify-start">
          <Button
            onClick={handleSubmit}
            className="hover:bg-secondary"
          >
            <Plus className="h-4 w-4 ml-2" />
            إضافة
          </Button>
          <Button
            variant="ghost"
            onClick={onClose}
            className="mr-2"
          >
            إلغاء
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}